import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_2011e831-9e14-4050-89e9-67f554973600/artifacts/1oxcap9f_Logo%20The%20Rig.svg";
const INFO_COLORS = ["#FF6B2B", "#2DC653", "#0A6E6E", "#FFD60A"];

export default function Contact() {
  const [contact, setContact] = useState(null);
  const [footer, setFooter] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = "Contact Us | The Rig - Coworking & Meeting Spaces";
    axios.get(`${API}/site-content`).then(r => {
      setContact(r.data.contact_page || {});
      setFooter(r.data.footer || {});
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in all required fields"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    } finally { setSending(false); }
  };

  if (!contact) return <div className="max-w-5xl mx-auto px-6 py-24"><div className="h-64 bg-gray-100 animate-pulse rounded-2xl" /></div>;

  const infoItems = [
    { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}` },
    { icon: Phone, label: "Phone", value: contact.phone, href: `tel:${contact.phone}` },
    { icon: MapPin, label: "Address", value: contact.address },
    { icon: Clock, label: "Hours", value: contact.hours },
  ].filter(i => i.value);

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <title>Contact Us | The Rig - Coworking & Meeting Spaces</title>
      <meta name="description" content={`Get in touch with The Rig. ${contact.email ? `Email us at ${contact.email}.` : ""} ${contact.hours ? `Open ${contact.hours}.` : ""} Book a tour or ask about our meeting rooms and office spaces.`} />
      <meta property="og:title" content="Contact The Rig - Coworking & Meeting Spaces" />
      <meta property="og:description" content="Have a question about our meeting rooms or office spaces? Get in touch with The Rig team." />
      <meta property="og:type" content="website" />

      {/* Header */}
      <section style={{ backgroundColor: "#1E1E1E" }} className="py-20 md:py-28" data-testid="contact-header">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block bg-rig-orange/20 text-rig-orange text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
            Contact Us
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-4">{contact.title || "Get in Touch"}</h1>
          <p className="text-white/40 max-w-lg mx-auto">{contact.subtitle || ""}</p>
        </div>
      </section>

      {/* Multi-color stripe */}
      <div className="flex h-1">
        <div className="flex-1" style={{ backgroundColor: "#FFD60A" }} />
        <div className="flex-1" style={{ backgroundColor: "#FF6B2B" }} />
        <div className="flex-1" style={{ backgroundColor: "#2DC653" }} />
        <div className="flex-1" style={{ backgroundColor: "#0A6E6E" }} />
      </div>

      {/* Content */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-rig-charcoal mb-6">Send us a message</h2>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-rig-charcoal/60 text-sm font-medium">Name *</Label>
                    <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Your name" required className="rounded-lg mt-1" data-testid="contact-name" />
                  </div>
                  <div>
                    <Label className="text-rig-charcoal/60 text-sm font-medium">Email *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="you@example.com" required className="rounded-lg mt-1" data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <Label className="text-rig-charcoal/60 text-sm font-medium">Subject</Label>
                  <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="How can we help?" className="rounded-lg mt-1" data-testid="contact-subject" />
                </div>
                <div>
                  <Label className="text-rig-charcoal/60 text-sm font-medium">Message *</Label>
                  <Textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Tell us more..." rows={5} required className="rounded-lg mt-1" data-testid="contact-message" />
                </div>
                <Button
                  type="submit"
                  disabled={sending}
                  className="bg-rig-orange text-white hover:bg-rig-orange/90 rounded-full px-8 py-3 font-bold shadow-lg shadow-rig-orange/25 w-full sm:w-auto"
                  data-testid="contact-submit-btn"
                >
                  <Send className="h-4 w-4 mr-2" />{sending ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-rig-charcoal mb-6">Contact Information</h2>
              <div className="space-y-5">
                {infoItems.map((item, i) => {
                  const color = INFO_COLORS[i % INFO_COLORS.length];
                  return (
                    <div key={i} className="flex items-start gap-4" data-testid={`contact-info-${i}`}>
                      <div className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                        <item.icon className="h-5 w-5" style={{ color }} strokeWidth={2} />
                      </div>
                      <div>
                        <p className="text-xs text-rig-charcoal/40 uppercase tracking-wider mb-1 font-medium">{item.label}</p>
                        {item.href ? (
                          <a href={item.href} className="text-rig-charcoal font-semibold hover:text-rig-orange transition-colors">{item.value}</a>
                        ) : (
                          <p className="text-rig-charcoal font-semibold">{item.value}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Map Embed */}
              {contact.map_embed && (
                <div className="mt-8 overflow-hidden rounded-2xl border border-gray-100 shadow-sm" data-testid="contact-map">
                  <div dangerouslySetInnerHTML={{ __html: contact.map_embed }} className="w-full h-64" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: "#1E1E1E" }}>
        <div className="flex h-1">
          <div className="flex-1" style={{ backgroundColor: "#FFD60A" }} />
          <div className="flex-1" style={{ backgroundColor: "#FF6B2B" }} />
          <div className="flex-1" style={{ backgroundColor: "#2DC653" }} />
          <div className="flex-1" style={{ backgroundColor: "#0A6E6E" }} />
        </div>
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <img src={LOGO_URL} alt="The Rig" className="h-12 invert mb-4" />
              <p className="text-white/40 text-sm max-w-xs leading-relaxed">{footer?.description || ""}</p>
            </div>
            <div>
              <h4 className="text-white font-heading font-bold text-sm uppercase tracking-wider mb-4">Quick Links</h4>
              <nav className="flex flex-col gap-2.5">
                {[{ to: "/", label: "Home" }, { to: "/spaces", label: "Spaces" }, { to: "/about", label: "About" }, { to: "/contact", label: "Contact" }].map(link => (
                  <Link key={link.to} to={link.to} className="text-white/40 hover:text-rig-yellow text-sm transition-colors">{link.label}</Link>
                ))}
              </nav>
            </div>
            <div>
              <h4 className="text-white font-heading font-bold text-sm uppercase tracking-wider mb-4">Contact</h4>
              <p className="text-white/40 text-sm">{footer?.email || "therig@ic-foundation.org"}</p>
            </div>
          </div>
          <div className="mt-14 pt-8 border-t border-white/10 text-white/25 text-xs">{footer?.copyright || ""}</div>
        </div>
      </footer>
    </div>
  );
}
